<script lang="ts">
import { defineComponent } from 'vue'
import DocumentsDataService from '@/services/DocumentsDataService'
import type Document from '@/types/Document'
import type ResponseData from '@/types/ResponseData'
import type { LaravelPaginatedResponse } from '@/types/ApiResponses'
import * as _ from 'lodash'
import * as FileSaver from 'file-saver'
import dayjs from 'dayjs'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

export default defineComponent({
  name: 'documents-table',
  props: ['hladaj'],
  data() {
    return {
      documents: [] as Document[],
      document: {} as Document,
      filterDocuments: '',
      loaded: false,
      selectedColumn: 'id',
      selectedDirection: 'desc',
      pagination: {} as LaravelPaginatedResponse<Document>['data'],
      pagesRange: 5
    }
  },
  methods: {
    formatDate(dateString: string) {
      const date = dayjs(dateString)
      return date.format('DD.MM.YYYY')
    },
    saveAs(filename: string, blob: Blob) {
      FileSaver.saveAs(blob, filename)
    },
    retrieveDocuments(page: number, type: string) {
      if (this.hladaj != null && this.hladaj != 'all') {
        DocumentsDataService.getAllDocumentsForCompany(this.hladaj, page)
        .then((response: ResponseData) => {
          const responseData = response.data as LaravelPaginatedResponse<Document>['data'];
          this.documents = responseData.data;
          this.pagination = responseData;
          this.loaded = true
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
      } else if (this.hladaj == undefined) {
        this.documents = []
        this.loaded = true
      } else {
        if (type == 'prev') {
          page--;
        } else if (type == 'next') {
          page++;
        }

        DocumentsDataService.getAllDocuments(page)
          .then((response: ResponseData) => {
            this.documents = response.data.data.data
            this.pagination = response.data.data
            this.loaded = true
          })
          .catch((e: Error) => {
            this.loaded = true
            toast.error('Chyba: ' + e.message)
          })
      }
    },
    refreshList() {
      this.retrieveDocuments(1, '')
    },
    downloadDocument(id: any) {
      DocumentsDataService.downloadFile(id)
        .then((response: ResponseData) => {
          const byteCharacters = atob(response.data)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: 'application/pdf' })

          this.saveAs('document', blob)
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
        })
    },
    setOrderResultsBy(column: any) {
      if (this.selectedColumn == column) {
        if (this.selectedDirection == 'desc') {
          this.selectedDirection = 'asc'
        } else {
          this.selectedDirection = 'desc'
        }
      } else {
        this.selectedDirection = 'asc'
      }
      this.selectedColumn = column
    },
  },
  computed: {
    pages() {
      const currentPage = this.pagination.current_page;
      const lastPage = this.pagination.last_page;
      const startPage = Math.max(1, currentPage - Math.floor(this.pagesRange / 2));
      const endPage = Math.min(lastPage, startPage + this.pagesRange - 1);

      return _.range(startPage, endPage + 1);
    },

    filteredDocuments() {
      return this.documents.filter(document => {
        const serial_number = document.serial_number.toLowerCase()
        const company_name = document.name.toLowerCase()
        const type = document.typeStr.toLowerCase()
        const subtype = document.subtypeStr.toLowerCase()
        const searchTerm = this.filterDocuments.toLowerCase()
        return (
          company_name.includes(searchTerm) ||
          serial_number.includes(searchTerm) ||
          type.includes(searchTerm) ||
          subtype.includes(searchTerm)
        )
      })
    },
    orderedItems() {
      switch (this.selectedColumn) {
        case 'id':
          return _.orderBy(this.filteredDocuments, ['id'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'company_name':
          return _.orderBy(
            this.filteredDocuments,
            ['company_name'],
            [this.selectedDirection.includes('asc') ? 'asc' : 'desc'],
          )
        case 'serial_number':
          return _.orderBy(
            this.filteredDocuments,
            ['serial_number'],
            [this.selectedDirection.includes('asc') ? 'asc' : 'desc'],
          )
        case 'type':
          return _.orderBy(this.filteredDocuments, ['type'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'subtype':
          return _.orderBy(this.filteredDocuments, ['subtype'], [this.selectedDirection.includes('asc') ? 'asc' : 'desc'])
        case 'date':
          return _.orderBy(
            this.filteredDocuments,
            ['updated_at'],
            [this.selectedDirection.includes('asc') ? 'asc' : 'desc'],
          )
        default:
          return _.orderBy(this.filteredDocuments, ['id'], ['asc'])
      }
    }
  },
  mounted() {
    this.retrieveDocuments(1, '')
  }
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
        <div class="py-4">
          <div class="flex flex-col">
            <div class="overflow-x-auto">
              <div class="flex flex-row justify-between">
                <div class="relative max-w-xs">
                  <label for="search" class="sr-only"> Hľadať </label>
                  <FormKit v-model="filterDocuments" type="search" name="search"
                    class="block w-full p-3 pl-10 text-sm border-gray-200 rounded-md focus:border-teal-500 focus:ring-teal-500 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400"
                    placeholder="Hľadať..." />
                </div>
              </div>
              <div class="p-1.5 w-full inline-block align-middle" v-if="!loaded">
                <div class="text-center">
                  <div role="status">
                    <svg class="inline mr-2 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-teal-600"
                      viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor" />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill" />
                    </svg>
                    <span class="sr-only">Loading...</span>
                  </div>
                </div>
              </div>
              <div class="columns" v-if="loaded && !documents.length">
                <p>No items found</p>
              </div>
              <div class="p-1.5 w-full inline-block align-middle" v-if="loaded && documents.length">
                <div class="overflow-hidden border rounded-lg">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th scope="col"
                          class="flex items-center px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center" :class="{ 'text-teal-500': selectedColumn == 'id' }">
                            ID
                            <button>
                              <svg @click="setOrderResultsBy('id')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center"
                            :class="{ 'text-teal-500': selectedColumn == 'company_name' }">
                            Spoločnosť
                            <button>
                              <svg @click="setOrderResultsBy('company_name')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg></button></span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center"
                            :class="{ 'text-teal-500': selectedColumn == 'serial_number' }">
                            Číslo dokladu
                            <button>
                              <svg @click="setOrderResultsBy('serial_number')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg></button></span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center" :class="{ 'text-teal-500': selectedColumn == 'type' }">
                            Typ
                            <button>
                              <svg @click="setOrderResultsBy('type')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg></button></span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center"
                            :class="{ 'text-teal-500': selectedColumn == 'subtype' }">
                            Podtyp
                            <button>
                              <svg @click="setOrderResultsBy('subtype')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg></button></span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-left text-gray-500 uppercase">
                          <span class="inline-flex items-center" :class="{ 'text-teal-500': selectedColumn == 'date' }">
                            Dátum zmeny
                            <button>
                              <svg @click="setOrderResultsBy('date')" xmlns="http://www.w3.org/2000/svg"
                                class="ml-1 w-3 h-3" aria-hidden="true" fill="currentColor" viewBox="0 0 320 512">
                                <path
                                  d="M27.66 224h264.7c24.6 0 36.89-29.78 19.54-47.12l-132.3-136.8c-5.406-5.406-12.47-8.107-19.53-8.107c-7.055 0-14.09 2.701-19.45 8.107L8.119 176.9C-9.229 194.2 3.055 224 27.66 224zM292.3 288H27.66c-24.6 0-36.89 29.77-19.54 47.12l132.5 136.8C145.9 477.3 152.1 480 160 480c7.053 0 14.12-2.703 19.53-8.109l132.3-136.8C329.2 317.8 316.9 288 292.3 288z" />
                              </svg>
                            </button>
                          </span>
                        </th>
                        <th scope="col" class="px-6 py-3 text-xs font-bold text-right text-gray-500 uppercase">
                          Stiahnuť PDF
                        </th>
                      </tr>
                    </thead>
                    <tbody class="divide-y divide-gray-200">
                      <tr class="hover:bg-gray-200 bg-white" v-for="document in orderedItems" :key="document.id">
                        <td class="px-6 py-4 text-sm font-medium text-gray-800">{{ document.id }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ document.name }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ document.serial_number }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ document.typeStr }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">{{ document.subtypeStr }}</td>
                        <td class="px-6 py-4 text-sm text-gray-800">
                          {{ formatDate(document.updated_at.toString()) }}
                        </td>
                        <td class="px-6 py-4 text-sm font-medium text-right">
                          <button @click="downloadDocument(document.id)" class="text-teal-500 hover:text-teal-700">
                            Stiahnuť
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
                    <div class="flex flex-1 justify-between sm:hidden">
                      <a href="#"
                        class="relative inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Previous</a>
                      <a href="#"
                        class="relative ml-3 inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Next</a>
                    </div>
                    <div class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                      <div>
                        <p class="text-sm text-gray-700">
                          Stránka
                          <span class="font-medium">{{ pagination.current_page }}</span>
                          z
                          <span class="font-medium">{{ pagination.last_page }}</span>
                        </p>
                      </div>
                      <div>
                        <nav class="isolate inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                          <button
                            class="relative inline-flex items-center rounded-l-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                            :disabled="pagination.current_page <= 1"
                            @click="retrieveDocuments(pagination.current_page, 'prev')">
                            <span class="sr-only">Previous</span>
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>

                          <!-- Dynamicky generované čísla stránok -->
                          <button v-for="pageNumber in pages" :key="pageNumber"
                            @click="retrieveDocuments(pageNumber, '')" :class="{
                              'z-10 bg-teal-50 border-teal-500 text-teal-600': pageNumber === pagination.current_page,
                              'border-gray-300 hover:bg-gray-50 focus:z-20': pageNumber !== pagination.current_page
                            }"
                            class="relative inline-flex items-center border text-gray-500 hover:bg-gray-50 bg-white px-4 py-2 text-sm font-medium">
                            {{ pageNumber }}
                          </button>

                          <button
                            class="relative inline-flex items-center rounded-r-md border border-gray-300 bg-white px-2 py-2 text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-20"
                            :disabled="pagination.current_page >= pagination.last_page"
                            @click="retrieveDocuments(pagination.current_page, 'next')">
                            <span class="sr-only">Next</span>
                            <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"
                              fill="currentColor" aria-hidden="true">
                              <path fill-rule="evenodd"
                                d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                                clip-rule="evenodd" />
                            </svg>
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
